import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtErr, money, fdate } from "../api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import MapView from "../components/MapView";
import InfoTip from "../components/InfoTip";
import FleetSection from "../components/FleetSection";
import JobInvoices from "../components/JobInvoices";
import { EmptyState, Stat, StatusPill, VEHICLE_LABELS, SERVICE_LABELS } from "../components/bits";
import { Navigation, Radio, ArrowRight, Map as MapIcon, List, Search, RotateCcw } from "lucide-react";

const NEXT = { assigned: "Heading to collection", picked_up: "Collected — start transit", in_transit: "Mark delivered" };

function ActiveJobRow({ job, onStatus }) {
  return (
    <div data-testid={`dash-job-${job.ref}`} className="cr-card cr-card-hover !p-3.5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="font-mono text-[11px] text-[#A09D95]">{job.ref}</p>
          <p className="text-sm font-semibold">{job.pickup?.city} → {job.delivery?.city}</p>
          <p className="text-xs text-[#78756E] mt-0.5">
            {VEHICLE_LABELS[job.vehicle_type]}{job.pickup_date ? ` · pickup ${fdate(job.pickup_date)}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill status={job.status} />
          {NEXT[job.status] && (
            <button data-testid={`dash-status-${job.ref}`} onClick={() => onStatus(job.id, job.status)}
              className="bg-[#1C1B1A] text-white text-xs font-semibold px-3.5 py-2 rounded-full hover:bg-[#2C2B2A] transition-colors">{NEXT[job.status]}</button>
          )}
          <Link to={`/jobs/${job.id}`} data-testid={`dash-view-${job.ref}`} className="text-xs font-semibold underline underline-offset-2">Open</Link>
        </div>
      </div>
    </div>
  );
}

function LoadCard({ job, onOpen }) {
  return (
    <div data-testid={`load-${job.ref}`} className="cr-card cr-card-hover !p-4 cursor-pointer" onClick={() => onOpen(job.id)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] text-[#A09D95]">{job.ref}</span>
            <StatusPill status={job.status} />
          </div>
          <p className="text-sm font-bold mt-1 truncate">
            {(job.pickup?.city || "—").toUpperCase()}{job.pickup?.postcode ? `, ${job.pickup.postcode}` : ""}
            <span className="text-[#A09D95] mx-1.5 font-normal">→</span>
            {(job.delivery?.city || "—").toUpperCase()}{job.delivery?.postcode ? `, ${job.delivery.postcode}` : ""}
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-[#78756E]">
            {job.pickup_date && <span>Pickup: <b className="text-[#1C1B1A]">{fdate(job.pickup_date)}</b></span>}
            {job.route_km != null && <span>Dist: <b className="text-[#1C1B1A]">{Math.round(job.route_km)} km</b></span>}
            {job.weight_kg != null && <span>Weight: <b className="text-[#1C1B1A]">{job.weight_kg} kg</b></span>}
            {job.pallets != null && <span>Packaging: <b className="text-[#1C1B1A]">{job.pallets} plt</b></span>}
            <span>Vehicle: <b className="text-[#1C1B1A]">{VEHICLE_LABELS[job.vehicle_type] || "Any"}</b></span>
          </div>
        </div>
        <div className="text-right shrink-0">
          {job.price_net != null && job.pricing_mode !== "bidding"
            ? <span className="cr-pill-yellow">{money(job.price_gross ?? job.price_net, job.currency)}</span>
            : <span className="cr-pill-outline">Open to bids</span>}
          <button data-testid={`quote-${job.ref}`} onClick={(e) => { e.stopPropagation(); onOpen(job.id); }}
            className="mt-2.5 block ml-auto bg-[#1C1B1A] text-white text-xs font-semibold px-4 py-2 rounded-full hover:bg-[#2C2B2A] transition-colors">
            {job.pricing_mode === "bidding" ? "Quote now" : "View & accept"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CourierDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("today");
  const [d, setD] = useState(null);
  const [docsReq, setDocsReq] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [board, setBoard] = useState({ jobs: [], total: 0, loading: true });
  const [view, setView] = useState("map");
  const [filters, setFilters] = useState({ city: "", delivery_city: "", vehicle_type: "", date_from: "", service_level: "" });

  const load = useCallback(() => {
    api.get("/courier/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/account/documents/required").then((r) => setDocsReq(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const loadBoard = useCallback(() => {
    const params = { assignment: "open", page_size: 50, sort: "pickup_date" };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/jobs/marketplace", { params })
      .then((r) => setBoard({ jobs: r.data.jobs, total: r.data.total, loading: false }))
      .catch(() => setBoard((b) => ({ ...b, loading: false })));
  }, [filters]);
  useEffect(() => { loadBoard(); const t = setInterval(loadBoard, 30000); return () => clearInterval(t); }, [loadBoard]);

  useEffect(() => {
    if (tab === "earnings") api.get("/courier/earnings").then((r) => setEarnings(r.data)).catch(() => {});
  }, [tab]);

  const quickStatus = async (jobId, status) => {
    const flow = { assigned: "picked_up", picked_up: "in_transit", in_transit: "delivered" };
    try { await api.post(`/jobs/${jobId}/status`, { status: flow[status] }); toast.success("Status updated"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const shareGps = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try { await api.post("/maps/driver-location", { lat: pos.coords.latitude, lng: pos.coords.longitude }); toast.success("GPS shared — companies can see you"); }
      catch (e) { toast.error(fmtErr(e)); }
    }, () => toast.error("Location permission denied"), { enableHighAccuracy: true });
  };

  const toggleAvailability = async () => {
    const cur = d?.availability?.available !== false;
    try { await api.post("/courier/availability", { available: !cur }); toast.success(!cur ? "You are available" : "You are offline"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  if (!d) return <p className="text-xs text-[#A09D95] py-20 text-center">Loading…</p>;
  const tabs = [["today", "Load board"], ["invoices", "Invoices"], ["earnings", "Earnings"], ["fleet", "Fleet & coverage"]];
  const available = d.availability?.available !== false;
  const pendingBids = d.bids_pending || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="courier-dashboard">
      <Seo title="Courier dashboard | CourierRadar" noindex />
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Hi, {user.name?.split(" ")[0]}</h1>
        <div className="flex items-center gap-2">
          <button data-testid="share-gps-btn" onClick={shareGps} className="cr-btn-outline !py-2 !px-3.5 !text-xs flex items-center gap-1.5">
            <Navigation className="w-3.5 h-3.5" /> Share GPS
          </button>
          <button data-testid="availability-toggle" onClick={toggleAvailability}
            className={`flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-full transition-all ${available ? "bg-[#1C1B1A] text-white" : "bg-white border border-[#E8E4D9] text-[#78756E]"}`}>
            <Radio className="w-3.5 h-3.5" /> {available ? "Available" : "Offline"}
          </button>
        </div>
      </div>

      <div className="inline-flex bg-[#EFECE2] p-1.5 rounded-full gap-1 mb-6">
        {tabs.map(([id, label]) => (
          <button key={id} data-testid={`courier-tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${tab === id ? "bg-[#1C1B1A] text-white shadow-sm" : "text-[#78756E] hover:text-[#1C1B1A]"}`}>{label}</button>
        ))}
      </div>

      {docsReq && !docsReq.ok && (
        <Link to="/account" data-testid="compliance-banner" className="block bg-[#1C1B1A] text-white rounded-[28px] p-4 mb-6 hover:bg-[#2C2B2A] transition-colors">
          <p className="text-sm font-bold">Documents required to stay compliant</p>
          <p className="text-xs text-white/60 mt-0.5">{docsReq.required.filter((r) => !r.verified).map((r) => r.doc_type.replace(/_/g, " ")).join(" · ")} → upload in your account</p>
        </Link>
      )}

      {tab === "today" && (
        <div className="space-y-6">
          {d.my_active.length > 0 && (
            <section data-testid="needs-action">
              <h2 className="text-sm font-bold mb-3 flex items-center">Needs action ({d.my_active.length})
                <InfoTip text="Your active jobs. Tap the black button to move each job to its next status." /></h2>
              <div className="space-y-2.5">{d.my_active.map((j) => <ActiveJobRow key={j.id} job={j} onStatus={quickStatus} />)}</div>
            </section>
          )}

          <section data-testid="load-board">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h2 className="text-sm font-bold flex items-center">Open loads ({board.total})
                <InfoTip text="Real jobs from subscribed companies. Filter on the left, quote from the list or tap a marker on the map." /></h2>
              <div className="inline-flex bg-white border border-[#E8E4D9] p-1 rounded-full gap-1">
                <button data-testid="board-map-view" onClick={() => setView("map")}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all ${view === "map" ? "bg-[#1C1B1A] text-white" : "text-[#78756E]"}`}><MapIcon className="w-3.5 h-3.5" />Map</button>
                <button data-testid="board-list-view" onClick={() => setView("list")}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all ${view === "list" ? "bg-[#1C1B1A] text-white" : "text-[#78756E]"}`}><List className="w-3.5 h-3.5" />List</button>
              </div>
            </div>
            <div className="grid lg:grid-cols-[240px_1fr] gap-4">
              <aside className="cr-card !p-4 h-fit space-y-2.5 lg:sticky lg:top-20" data-testid="board-filters">
                <p className="cr-label flex items-center gap-1.5"><Search className="w-3 h-3" /> Search loads</p>
                <input data-testid="bf-from" placeholder="From — city" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} className="cr-input !py-2 !text-xs" />
                <input data-testid="bf-to" placeholder="To — city" value={filters.delivery_city} onChange={(e) => setFilters({ ...filters, delivery_city: e.target.value })} className="cr-input !py-2 !text-xs" />
                <select data-testid="bf-vehicle" value={filters.vehicle_type} onChange={(e) => setFilters({ ...filters, vehicle_type: e.target.value })} className="cr-input !py-2 !text-xs">
                  <option value="">Any vehicle</option>
                  {Object.entries(VEHICLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <select data-testid="bf-service" value={filters.service_level} onChange={(e) => setFilters({ ...filters, service_level: e.target.value })} className="cr-input !py-2 !text-xs">
                  <option value="">Any service</option>
                  {Object.entries(SERVICE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <input data-testid="bf-date" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="cr-input !py-2 !text-xs" />
                <button data-testid="bf-reset" onClick={() => setFilters({ city: "", delivery_city: "", vehicle_type: "", date_from: "", service_level: "" })}
                  className="text-xs text-[#78756E] hover:text-[#1C1B1A] flex items-center gap-1 transition-colors"><RotateCcw className="w-3 h-3" /> Reset</button>
              </aside>
              <div>
                {view === "map" ? (
                  <div className="rounded-[28px] overflow-hidden border border-[#E8E4D9]" data-testid="board-map">
                    <MapView height="520px" jobs={[...d.my_active, ...board.jobs]} onSelectJob={(id) => navigate(`/jobs/${id}`)} />
                  </div>
                ) : (
                  <div className="space-y-2.5 max-h-[560px] cr-scroll pr-1" data-testid="board-list">
                    {board.loading && <p className="text-xs text-[#A09D95] py-10 text-center">Loading loads…</p>}
                    {!board.loading && board.jobs.length === 0 && <EmptyState title="No loads match" body="Adjust or reset the search filters." />}
                    {board.jobs.map((j) => <LoadCard key={j.id} job={j} onOpen={(id) => navigate(`/jobs/${id}`)} />)}
                  </div>
                )}
              </div>
            </div>
          </section>

          <div className="grid lg:grid-cols-3 gap-4">
            <Stat label="This month" value={money(d.earnings?.month ?? 0, "GBP")} sub={`${d.earnings?.completed ?? 0} deliveries completed`} />
            <Stat label="Total earned" value={money(d.earnings?.total ?? 0, "GBP")} sub="all time" />
            <section className="cr-card !p-4" data-testid="bids-pending">
              <h3 className="text-xs font-bold mb-2">Bids awaiting decision ({pendingBids.length})</h3>
              {pendingBids.length === 0 && <p className="text-[11px] text-[#A09D95]">No pending bids.</p>}
              {pendingBids.slice(0, 4).map((b) => (
                <Link key={b.id} to={`/jobs/${b.job_id}`} className="flex justify-between text-xs py-1.5 border-b border-[#F6F3EA] last:border-0 hover:text-black">
                  <span className="font-mono">{b.job_ref}</span><span className="font-semibold">{money(b.amount, b.currency)}</span>
                </Link>
              ))}
            </section>
          </div>
        </div>
      )}

      {tab === "invoices" && <JobInvoices />}

      {tab === "earnings" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="earnings-tab">
          <Stat label="This month" value={money(d.earnings?.month ?? 0, "GBP")} sub={`${d.earnings?.completed ?? 0} deliveries`} />
          <Stat label="Total earned" value={money(d.earnings?.total ?? 0, "GBP")} sub="all time" />
          <Stat label="Completed" value={d.earnings?.completed ?? 0} sub="deliveries" />
          <Stat label="Rating" value={user.rating_avg ? `${user.rating_avg.toFixed(1)}★` : "—"} sub={`${user.rating_count || 0} reviews`} />
          {earnings && (
            <section className="cr-card !p-5 sm:col-span-2 lg:col-span-4" data-testid="earnings-by-month">
              <h3 className="text-sm font-bold mb-3">By month</h3>
              {earnings.by_month.length === 0 && <p className="text-xs text-[#A09D95]">No completed deliveries yet.</p>}
              {earnings.by_month.map((m) => (
                <div key={m.month} className="flex justify-between text-sm py-2 border-b border-[#F6F3EA] last:border-0">
                  <span>{m.month}</span><span className="font-semibold">{money(m.total, "GBP")} · {m.jobs} jobs</span>
                </div>
              ))}
            </section>
          )}
        </div>
      )}

      {tab === "fleet" && (
        <div className="grid lg:grid-cols-2 gap-6" data-testid="fleet-tab">
          <section className="cr-card !p-5">
            <h2 className="text-sm font-bold mb-3 flex items-center">Availability<InfoTip text="Set offline when you can't take jobs. Add the countries you cover to get matched loads." /></h2>
            <label className="flex items-center gap-2 text-sm mb-4">
              <input type="checkbox" data-testid="availability-checkbox" checked={available} onChange={toggleAvailability} className="accent-[#1C1B1A]" /> Available for jobs
            </label>
            <p className="cr-label mb-2">Coverage countries</p>
            <div className="flex flex-wrap gap-1.5">
              {(d.availability?.coverage_countries || []).map((c) => (
                <span key={c} className="cr-pill-outline flex items-center gap-1">{c}
                  <button data-testid={`coverage-remove-${c}`} onClick={async () => {
                    await api.post("/courier/availability", { coverage_remove: c }); load();
                  }} className="text-[#A09D95] hover:text-[#1C1B1A]">×</button>
                </span>
              ))}
            </div>
            <form className="flex gap-2 mt-3" onSubmit={async (e) => {
              e.preventDefault();
              const code = e.target.country.value.toUpperCase();
              if (!code) return;
              await api.post("/courier/availability", { coverage_add: code }); e.target.reset(); load();
            }}>
              <input name="country" data-testid="coverage-add-input" placeholder="Add country (e.g. DE)" maxLength={2} className="cr-input w-28 uppercase" />
              <button data-testid="coverage-add-btn" className="cr-btn-dark !py-2 !px-4 !text-xs">Add</button>
            </form>
          </section>
          <FleetSection defaultType="van_small" idPrefix="vehicle" tip="Register your vehicles for capacity matching." />
        </div>
      )}
    </div>
  );
}
